import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Overline, SectionTitle, Accent, Reveal } from "../Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

export { Overline, SectionTitle, Accent, Reveal };

export const ServiceHero = ({ overline, title, sub, bullets = [], primary, secondary, image, testid }) => (
  <section className="relative overflow-hidden grain pt-[72px]" data-testid={testid}>
    <div className="absolute -z-0 top-1/4 -left-40 w-[520px] h-[520px] rounded-full bg-[#00E676]/12 blur-[130px]" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-16 sm:pt-28 sm:pb-20 grid lg:grid-cols-12 gap-12 items-center">
      <div className={image ? "lg:col-span-7" : "lg:col-span-12"}>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/30 bg-[#00E676]/10 px-4 py-1.5 text-xs font-mono2 uppercase tracking-[0.2em] text-[#00E676] mb-8"
        >
          {overline}
        </motion.p>
        <h1 className="font-heading font-extrabold tracking-tight leading-[1.06] text-3xl sm:text-5xl lg:text-6xl text-white">
          <span className="mask-line"><span style={{ animationDelay: "0.15s" }}>{title[0]}</span></span>
          {title[1] && <span className="mask-line"><span style={{ animationDelay: "0.3s" }} className="text-[#00E676] text-glow">{title[1]}</span></span>}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-emerald-100/70"
        >
          {sub}
        </motion.p>
        {bullets.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-xs sm:text-sm text-emerald-100/60"
          >
            {bullets.map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5">
                <ShieldCheck size={14} className="text-[#00E676]" /> {b}
              </span>
            ))}
          </motion.div>
        )}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-9 flex flex-wrap gap-4"
        >
          {primary}
          {secondary}
        </motion.div>
      </div>
      {image && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-[#00E676]/15 blur-2xl" />
            <img src={image} alt="" className="relative w-full rounded-3xl border border-[#00E676]/25 object-cover aspect-[4/3]" loading="eager" />
          </div>
        </motion.div>
      )}
    </div>
  </section>
);

export const CtaPrimary = ({ href, children, testid, external = true }) => (
  <a
    href={href}
    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    data-testid={testid}
    className="inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C853] text-[#04120E] font-bold px-7 py-4 shadow-[0_0_26px_rgba(0,230,118,0.4)] transition-[background-color,transform] duration-300 hover:scale-[1.03]"
  >
    {children} <ArrowRight size={17} />
  </a>
);

export const CtaGhost = ({ href, children, testid, external = true }) => (
  <a
    href={href}
    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    data-testid={testid}
    className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/35 bg-emerald-950/50 backdrop-blur-sm text-[#00E676] font-semibold px-7 py-4 hover:bg-emerald-900/60 transition-[background-color,transform] duration-300 hover:scale-[1.03]"
  >
    {children}
  </a>
);

export const FeatureGrid = ({ items, testid }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={testid}>
    {items.map((f, i) => (
      <Reveal key={f.title} delay={(i % 3) * 0.08}>
        <div className="h-full rounded-2xl border border-white/10 bg-[#0B261E]/60 p-7 transition-[border-color,transform] duration-300 hover:border-[#00E676]/45 hover:-translate-y-1">
          {f.icon && (
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#00E676]/12 border border-[#00E676]/25 text-[#00E676] mb-5">
              <f.icon size={20} />
            </span>
          )}
          <h3 className="font-heading text-base sm:text-lg font-bold text-white">{f.title}</h3>
          <p className="mt-2 text-sm text-emerald-100/55 leading-relaxed">{f.text}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

export const StepsList = ({ steps, testid }) => (
  <div className="space-y-5" data-testid={testid}>
    {steps.map((s, i) => (
      <Reveal key={s.n} delay={i * 0.08}>
        <div className="flex gap-5 items-start group rounded-2xl border border-white/10 bg-[#0B261E]/50 p-6 transition-[border-color] duration-300 hover:border-[#00E676]/40">
          <span className="shrink-0 grid place-items-center w-12 h-12 rounded-full bg-[#00E676] text-[#04120E] font-heading font-extrabold text-lg shadow-[0_0_18px_rgba(0,230,118,0.35)] transition-transform duration-300 group-hover:scale-110">
            {s.n}
          </span>
          <div className="pt-1">
            <h3 className="font-heading text-lg font-bold text-white">{s.title}</h3>
            <p className="mt-1.5 text-sm text-emerald-100/55 leading-relaxed">{s.text}</p>
          </div>
        </div>
      </Reveal>
    ))}
  </div>
);

export const CompareTable = ({ leftName, rightName, rows, note, testid }) => (
  <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid={testid}>
    <div className="grid grid-cols-3 text-xs sm:text-sm font-bold bg-[#0B261E]/80 border-b border-white/10">
      <span className="px-4 sm:px-6 py-4 text-emerald-100/50" />
      <span className="px-4 sm:px-6 py-4 text-[#00E676] border-l border-white/10">{leftName}</span>
      <span className="px-4 sm:px-6 py-4 text-emerald-100/45 border-l border-white/10">{rightName}</span>
    </div>
    {rows.map((r, i) => (
      <div key={i} className={`grid grid-cols-3 text-xs sm:text-sm ${i % 2 ? "bg-white/[0.02]" : ""}`}>
        <span className="px-4 sm:px-6 py-3.5 font-semibold text-white">{r[0]}</span>
        <span className="px-4 sm:px-6 py-3.5 text-emerald-100/80 border-l border-[#00E676]/15">{r[1]}</span>
        <span className="px-4 sm:px-6 py-3.5 text-emerald-100/40 border-l border-white/10">{r[2]}</span>
      </div>
    ))}
    {note && <p className="px-4 sm:px-6 py-4 text-[11px] text-emerald-100/35 border-t border-white/10">{note}</p>}
  </div>
);

export const FaqBlock = ({ items, overline = "Dúvidas", title, testid }) => (
  <section className="max-w-4xl mx-auto px-5 sm:px-8 py-20 sm:py-24" data-testid={testid}>
    <Reveal>
      <Overline>{overline}</Overline>
      <SectionTitle>{title}</SectionTitle>
    </Reveal>
    <Reveal delay={0.1}>
      <Accordion type="single" collapsible className="mt-10 space-y-3">
        {items.map((item, i) => (
          <AccordionItem
            key={i}
            value={`faq-${i}`}
            className="rounded-2xl border border-white/10 bg-[#0B261E]/60 px-6 data-[state=open]:border-[#00E676]/40 transition-colors duration-300"
          >
            <AccordionTrigger className="text-left font-heading font-semibold text-white hover:text-[#00E676] hover:no-underline py-5 text-sm sm:text-base">
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-emerald-100/60 leading-relaxed pb-6">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Reveal>
  </section>
);

export const Section = ({ children, className = "", testid }) => (
  <section className={`py-20 sm:py-28 ${className}`} data-testid={testid}>
    <div className="max-w-7xl mx-auto px-5 sm:px-8">{children}</div>
  </section>
);
